import React, { useState, useEffect } from 'react';
import { Routes, Route, useParams, useNavigate } from 'react-router-dom';
import { TemplateProvider } from '../context/TemplateContext';
import HomePage from '../components/HomePage';
import TemplateDetail from '../components/TemplateDetail';
import CameraCapture from '../components/CameraCapture';
import NotFound from '../components/NotFound';
import { useTemplate } from '../context/TemplateContext';
import { StyleTemplate } from '../types/template';

const AppRouter: React.FC = () => {
  // 创建应用级的状态来存储用户照片
  const [userPhotoCache, setUserPhotoCache] = useState<{[key: string]: string}>({});
  
  // 更新用户照片的函数
  const updateUserPhoto = (templateId: string, photoData: string) => {
    setUserPhotoCache(prev => ({
      ...prev,
      [templateId]: photoData
    }));
    console.log(`已将照片缓存到templateId: ${templateId}, 照片长度: ${photoData.length}`);
  };

  // 清除用户照片的函数
  const clearUserPhoto = (templateId: string) => {
    setUserPhotoCache(prev => {
      const newCache = { ...prev };
      delete newCache[templateId];
      return newCache;
    });
  };

  return (
    <TemplateProvider>
      <Routes>
        {/* 首页路由 */}
        <Route path="/" element={<HomePage />} />
        
        {/* 模板详情页路由 */}
        <Route path="/template/:id" element={
          <TemplateDetailWrapper 
            userPhotoCache={userPhotoCache} 
            clearUserPhoto={clearUserPhoto}
          />
        } />
        
        {/* 摄像头拍照页路由 */}
        <Route path="/camera/:templateId" element={
          <CameraCaptureWrapper updateUserPhoto={updateUserPhoto} />
        } />
        
        {/* 404 页面 - 必须放在最后 */}
        <Route path="*" element={<NotFound />} />
      </Routes>
    </TemplateProvider>
  );
};

// 模板详情页包装器，处理路由参数
interface TemplateDetailWrapperProps {
  userPhotoCache: {[key: string]: string};
  clearUserPhoto: (templateId: string) => void;
}

const TemplateDetailWrapper: React.FC<TemplateDetailWrapperProps> = ({ userPhotoCache, clearUserPhoto }) => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { getTemplateById } = useTemplate();
  
  // 从缓存中获取用户照片数据
  const userPhoto = id ? userPhotoCache[id] : undefined;
  // console.log('TemplateDetailWrapper - 从缓存获取的照片:', userPhoto ? '有照片数据，长度:' + userPhoto.length : '无照片数据');

  if (!id) {
    // 如果没有ID，返回首页
    navigate('/');
    return null;
  }

  const template = getTemplateById(id);

  if (!template) {
    // 如果找不到模板，显示错误或返回首页
    return (
      <div style={{ 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center', 
        justifyContent: 'center', 
        height: '100vh',
        padding: '20px',
        textAlign: 'center'
      }}>
        <h2>模板不存在</h2>
        <p>请求的模板未找到，可能已被删除或不存在。</p>
        <button 
          onClick={() => navigate('/')}
          style={{
            padding: '10px 20px',
            backgroundColor: '#007acc',
            color: 'white',
            border: 'none',
            borderRadius: '5px',
            cursor: 'pointer',
            fontSize: '16px'
          }}
        >
          返回首页
        </button>
      </div>
    );
  }

  const handleBack = () => {
    // 清除当前模板的照片缓存
    if (id) {
      clearUserPhoto(id);
    }
    navigate('/');
  };

  return (
    <TemplateDetail 
      template={template} 
      onBack={handleBack}
      userPhoto={userPhoto}
    />
  );
};

// 摄像头拍照页包装器，处理路由参数
interface CameraCaptureWrapperProps {
  updateUserPhoto: (templateId: string, photoData: string) => void;
}

const CameraCaptureWrapper: React.FC<CameraCaptureWrapperProps> = ({ updateUserPhoto }) => {
  const { templateId } = useParams<{ templateId: string }>();
  const navigate = useNavigate();
  const { getTemplateById, addTemplate } = useTemplate();
  const [loading, setLoading] = useState(true);
  const [template, setTemplate] = useState<StyleTemplate | null>(null);

  // 获取模板数据，先从上下文中查找，如果没有则直接从mockData获取
  useEffect(() => {
    if (!templateId) {
      navigate('/');
      return;
    }

    // 首先从Context中查找模板
    const existingTemplate = getTemplateById(templateId);
    if (existingTemplate) {
      setTemplate(existingTemplate);
      setLoading(false);
      return;
    }

    // 如果Context中没有，则直接从mockData中获取
    const fetchTemplate = async () => {
      try {
        setLoading(true);
        // 导入mockData中的函数来获取单个模板
        const { fetchTemplateById } = await import('../data/mockData');
        const fetchedTemplate = await fetchTemplateById(templateId);
        
        if (fetchedTemplate) {
          // 添加到Context中，以便后续使用
          addTemplate(fetchedTemplate);
          setTemplate(fetchedTemplate);
        } else {
          console.error('Template not found with id:', templateId);
          navigate('/');
        }
      } catch (error) {
        console.error('Error fetching template:', error);
        navigate('/');
      } finally {
        setLoading(false);
      }
    };

    fetchTemplate();
  }, [templateId, getTemplateById, addTemplate, navigate]);

  // 加载状态显示
  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center', 
        justifyContent: 'center', 
        height: '100vh' 
      }}>
        <div>加载中...</div>
      </div>
    );
  }

  if (!template) {
    // 如果找不到模板，返回首页
    navigate('/');
    return null;
  }

  const handleBack = () => {
    // 返回到模板详情页
    navigate(`/template/${templateId}`);
  };

  const handleCapture = (imageData: string) => {
    // 使用父组件传递的方法更新照片缓存
    if (!templateId) {
      console.error('Template ID is not defined');
      return;
    }
    
    updateUserPhoto(templateId, imageData);
    
    // 导航到模板详情页面
    navigate(`/template/${templateId}`);
  };

  return (
    <CameraCapture
      onBack={handleBack}
      onCapture={handleCapture}
      templateTitle={template.name}
    />
  );
};

export default AppRouter;
